<template>
  <ion-page id="signin-page">
    <ion-content>
      <TopLine :style="styleTopline" :title="signInTitle" :microphoneIsMuted="microphoneIsMuted" @topline-click="toplineClick"/>
    
      <div :class="{ 'text-dark' : isDark,'text-light' : !isDark }" :style="styleMiddle" :key="menuTrigger">
        <ion-item>
          <h5>{{ message }}</h5>
        </ion-item>

        <div v-show="showState == SHOW_LOGIN">

          <ionItem>
            <ion-input :label="emailString" label-placement="fixed" 
                      :placeholder="emailPlaceholderString" :value="email"
                      @ionInput="emailInputChange">
            </ion-input>
          </ionItem>

          <ionItem>
            <ion-input :label="passwordString" type="password" label-placement="fixed" 
                    :placeholder="passwordPlaceholderString" :value="password"
                    @ionInput="passwordInputChange">
              <ion-input-password-toggle slot="end"></ion-input-password-toggle>
            </ion-input>
          </ionItem>
        </div>

        <div v-show="showState == SHOW_ENTER_RESET_CODE">
          <ion-item>
            <ion-label>
              For : {{ email }}
            </ion-label>
          </ion-item>
          <ion-item>
            <ion-input :label="resetString" label-placement="fixed" 
                      :placeholder="resetPlaceholderString" :value="resetCode"
                      @ionInput="resetCodeInputChange">
            </ion-input>
          </ion-item>
          <ion-item>
            <ion-button :disabled="resetCode.length != 6" color="primary" size="large" @click="verifyPasswordResetCode()">
                                {{ translatePhrase("Verify Reset Code") }}
            </ion-button>
          </ion-item>
        </div>
        
        <div v-show="showState == SHOW_ENTER_NEW_PASSWORD">
          <ion-label>
            For : {{ email }}
          </ion-label>
          <ion-item>
            <ion-input :label="passwordString" type="password" label-placement="fixed" 
                    :placeholder="passwordPlaceholderString" :value="password"
                    @ionInput="passwordInputChange">
              <ion-input-password-toggle slot="end"></ion-input-password-toggle>
            </ion-input>
          </ion-item>
          <ion-item>
            <ion-input :label="passwordString" type="password" label-placement="fixed" 
                    :placeholder="passwordPlaceholderString" :value="password2"
                    @ionInput="password2InputChange">
              <ion-input-password-toggle slot="end"></ion-input-password-toggle>

            </ion-input>
          </ion-item>

          <ion-item>
            <ion-checkbox label-placement="end" @ionChange="resetCookie = !resetCookie">
              <span class="ion-text-wrap">{{ translatePhrase("Cancel any open sessions on other devices. (Not needed if you only forgot your password)") }}</span>
            </ion-checkbox>
          </ion-item>
          <ion-item>
            <ion-button expand="block" size="large" @click="doReset()" >{{ translateEnglishWords("Reset Password") }}</ion-button>
          </ion-item>        
        </div>
      </div>
      <ToolBar :style="styleToolbar" :elements="toolbarElements" :trigger="toolbarTrigger" @toolbar-click="toolbarClick" />
    </ion-content>
  </ion-page>
  
</template>

<script setup>

  import { IonCheckbox,IonLabel,IonInputPasswordToggle,IonGrid,IonRow,IonCol,IonButton,IonItem,IonInput,IonPage, IonContent, modalController } from '@ionic/vue';
  import { computed } from "@vue/reactivity"

  import { ref , watch } from 'vue';
  import { useGetData , generateErrorAlertText } from '@/composables/getData.js'
  import { useConnectionStore } from '@/stores/connectionStore';
  import { useWindowSizeStore } from '@/stores/windowSizeStore';

  import { doStartupCheck } from '@/composables/startupCheck.js'
  import ToolBar from '@/components/ToolBar.vue'
  import { parseWordsSaid } from '@/composables/wordlistSupport.js'

   import { buildStyleToolbar, buildStyleTopline,buildStyleMiddle,availableWidthHeight, 
    calculateChessBoardRect, isScreenPortraitOrientation } from '@/composables/screenSize'

  import TopLine from '@/components/TopLine.vue'
  import { translateEnglishWords, translatePhrase } from '@/composables/translation.js';

  const emit = defineEmits(['where-next','word-list','mic-is-muted'])

  const props = defineProps({
      wordsSaid : {
        type : String,
        default : ""
      },
      wordsSaidTrigger : {
        type : Number,
        default : 0
      }
    })

  const wordsSaid = computed({get() {return props.wordsSaid}})
  const wordsSaidTrigger = computed({get() {return props.wordsSaidTrigger}})

  const connectionStore = useConnectionStore()
  const windowSizeStore = useWindowSizeStore()
  
  const isDark = ref(connectionStore.getDarkmode != "light")
  const menuTrigger = ref(0)

  const cookie = connectionStore.getCookie
  const dummy = connectionStore.getDummy

  const subjectForEmail = "Verbal Chess - " + translatePhrase("Password Reset Request")
  const toEmail = "Verbal Chess" + translateEnglishWords("User")
  const greetingForEmail = translateEnglishWords("Hi")
  const topPartOfEmail = translatePhrase("To reset your password within the 'Verbal Chess' website, either click on the link below or copy it into your browser and it will take you to a password reset page.")
  const middlePartOfEmail = translatePhrase("If that doesn't work, this is the old link that does the same thing but uses the Chessvis.com site. Then you can manually go back to the new web site.")
  const endPartOfEmail = translatePhrase("If you have any questions, please email me at")

  // if user is logged in, go to home screen
  if (cookie != "" && dummy == 0) {
    emit("where-next","/")

    //router.replace({ path: '/' })
  }

  const wordlistLogin = "command done sign log in forgot password verify zero one two three four five six seven eight nine mute listen"
  const wordlist = ref(wordlistLogin)
  emit("word-list",wordlistLogin)
  const wordlistTrigger = ref(0)

  const emailStorage = connectionStore.getEmailAddress
  const email = ref( (emailStorage == null) ? "" : emailStorage)
  
  const password = ref("")
  const password2 = ref("")
  const resetCookie = ref(false)

  const resetCode = ref("")

  const signInTitle = ref(translateEnglishWords("Sign In"))
  const emailString = ref(translateEnglishWords("Email"))
  const passwordString = ref(translateEnglishWords("Password"))
  const emailPlaceholderString = ref(translateEnglishWords("Enter email"))
  const passwordPlaceholderString = ref(translateEnglishWords("Enter password"))

  const resetString = ref(translateEnglishWords("Reset Code"))
  const resetPlaceholderString = ref(translateEnglishWords("Enter Reset Code"))

  const message = ref(translatePhrase("Please Sign In -- (Note: Email/Password same as Chessvis)"))
  const showEmailPassword = ref(true)

  const SHOW_LOGIN = 0
  const SHOW_ENTER_RESET_CODE = 1
  const SHOW_ENTER_NEW_PASSWORD = 2

  const showState = ref(SHOW_LOGIN)

  const TOOLBAR_LOGIN = 0
  const TOOLBAR_FORGOT_PASSWORD = 1

  const originalToolbarElements = [
    { index : TOOLBAR_LOGIN, label : translateEnglishWords("Sign in") ,disabled : false},
    { index : TOOLBAR_FORGOT_PASSWORD, label : translateEnglishWords("Forgot Password") ,disabled : false},
  ]

  const toolbarElements = ref(originalToolbarElements)

  const toolbarTrigger = ref(0)

  const styleToolbar = ref(buildStyleToolbar(availableWidthHeight()))
  const styleTopline = ref(buildStyleTopline(availableWidthHeight()))
  const styleMiddle = ref(buildStyleMiddle(availableWidthHeight()))

  const microphoneIsMuted = ref(connectionStore.getMicrophoneIsMuted)
  emit("mic-is-muted",microphoneIsMuted.value)

  watch(wordsSaidTrigger, (newVal,oldVal) => {
     //console.log('*********** words said ',props.wordsSaid)
     processWords(props.wordsSaid)
   })

  watch(() => windowSizeStore.getWindowSize, () => {
    styleToolbar.value = buildStyleToolbar(availableWidthHeight())
    styleTopline.value = buildStyleTopline(availableWidthHeight())
    styleMiddle.value = buildStyleMiddle(availableWidthHeight())
    menuTrigger.value++
  })

  function toplineClick(whatClicked) {
    //console.log("topline",whatClicked)
    switch (whatClicked) {
      case "done" :
        processWords("command done")
        break;
      case "microphone" :
        microphoneIsMuted.value = !microphoneIsMuted.value 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(microphoneIsMuted.value)
        break

    }
  }

  function emailInputChange(ev) {
    //console.log("email ",ev)
    const value = ev.target.value;
    ///console.log("email",value)
    email.value = value.trim()
  }

  function passwordInputChange(ev) {
    //console.log("password ",ev)
    const value = ev.target.value;
    //console.log("password",value)
    password.value = value.trim()
  }

  function password2InputChange(ev) {
    //console.log("password2 ",ev)
    const value = ev.target.value;
    //console.log("password2",value)
    password2.value = value.trim()
  }

  function resetCodeInputChange(ev) {
    const value = ev.target.value;
    console.log("resetcodeinputchange",value)
    resetCode.value = value.trim()
  }

  function processJsonResponse (json) {
    //console.log(" in callback",json)
    if (json.hasOwnProperty('error')) {
      message.value = generateErrorAlertText(json)
      return;
    }
    switch (json['stat']) {
      case 'e' :
        message.value = translatePhrase("Email is not in the system")
        break;
      case 'p' :
        message.value = translatePhrase("Password is incorrect")
        break;
      case 'g' :
        const cookie = json['cookie']
        connectionStore.setUserCookieDummy(cookie,"0")
        connectionStore.setEmailAddress(email.value)

        emit("where-next","/main_from_login")

        break
    }
  }

  function doLogin () {
    if (email.value == "") {
      message.value = translatePhrase("Please enter your email address")
      return
    }
    if (password.value == "") {
      message.value = translatePhrase("Please enter your password")
      return
    }
    message.value = translateEnglishWords("checking") + "...."
    var formData = { e : email.value , p: password.value }
    useGetData("verbal_login.php", formData, processJsonResponse)
  }

  function processForgotJsonResponse (json) {
    console.log(" in forgot callback",json)
    if (json.hasOwnProperty('error')) {
      message.value = generateErrorAlertText(json)
      return;
    }
    switch (json['stat']) {
      case 'e' :
        message.value = translatePhrase("Email is not in the system")
        break;
      case 'g' :
        message.value = translatePhrase("Password reset email containing code sent")
        break
    }
    showState.value = SHOW_ENTER_RESET_CODE
    toolbarElements.value = []
    toolbarTrigger.value++
  }

  function doForgot () {
    if (email.value == "") {
      message.value = translateEnglishWords("Please enter your email address")
      return
    }

    const subjectForEmail = "Verbal Chess - " + translatePhrase("Password Reset Code")
    const toEmail = "Verbal Chess" + translateEnglishWords("User")
    const greetingForEmail = translateEnglishWords("Hi")
    const topPartOfEmail = translatePhrase("To reset your password for Chessvis, Verbal Chess and Chessvis AI, enter this code on the screen.")
    const middlePartOfEmail = translatePhrase("Note: code expires in 10 minutes.")
    const codeString = translateEnglishWords("Code")
    const endPartOfEmail = translatePhrase("Thanks for using my program.")

    message.value = translateEnglishWords("checking") + "...."
    var formData = { 
      e : email.value,
      subject : subjectForEmail,
      to_name : toEmail,
      greeting : greetingForEmail,
      top : topPartOfEmail,
      middle : middlePartOfEmail,
      code_string : codeString,
      end : endPartOfEmail
    }
    console.log("formdata",formData)
    useGetData("verbal_password_reset_code.php", formData, processForgotJsonResponse)
  }

  function toolbarClick(arrayIndex) {
    switch (arrayIndex) {
      case TOOLBAR_LOGIN :
        processWords("command log in ")
        break
      case TOOLBAR_FORGOT_PASSWORD :
        processWords("command forgot")
        break
    }
  }

  function processWords(text) {

    //console.log("text in Logoff ",text)
    const returnArray = parseWordsSaid(text,parseCommands)
    //console.log("command found",returnArray)

    switch (returnArray[0]) {
      case "done" :
        emit("where-next","/accountmenu")
        break
      case "login" :
        doLogin()
        break
      case "forgot" :
        doForgot()
        break
      case "verify" :
        if (resetCode.value.length == 6)
          verifyPasswordResetCode()
        break
    }
  }

  function parseCommands(words,currentWordIndex) {
    
    console.log("in parse commands",words,currentWordIndex)
    var currentWord = (words.length > currentWordIndex) ? words[currentWordIndex] : "xyz"
    var nextWord = (words.length > currentWordIndex+1) ? words[currentWordIndex+1] : "xyz"
    var nextNextWord = (words.length > currentWordIndex+2) ? words[currentWordIndex+2] : "xyz"

    for (var i = currentWordIndex; i < words.length; i++) {
        console.log("CurrentWord",currentWord,nextWord,nextNextWord,resetCode.value)
        if (currentWord == "ignore")
          return ["ignore",""];
        if (currentWord == "done")
          return ["done",""]
        if ((currentWord == "log" || currentWord == "sign") && nextWord == "in")
          return ["login",""]
        if (currentWord == "forgot")
          return ["forgot",""]
        if (currentWord == "verify")
          return ["verify",""]

        if (currentWord == "zero")
          resetCode.value += "0"
        else
        if (currentWord == "one")
          resetCode.value += "1"
        else    
        if (currentWord == "two")
          resetCode.value += "2"
        else    
        if (currentWord == "three")
          resetCode.value += "3"
        else
        if (currentWord == "four")
          resetCode.value += "4"
        else    
        if (currentWord == "five")
          resetCode.value += "5"
        else    
        if (currentWord == "six")
          resetCode.value += "6"
        else    
        if (currentWord == "seven")
          resetCode.value += "7"
        else     
        if (currentWord == "eight")
          resetCode.value += "8"
        else
        if (currentWord == "nine")
          resetCode.value += "9"
      
        currentWord = nextWord;
        nextWord = nextNextWord;
        nextNextWord = (i < words.length - 3) ? words[i + 3] : "xyz";
    }
    return ["ignore",""];
  }

  function processVerifyPasswordResetCodeJsonResponse(json) {
  console.log(" in verify password reset callback",json)
  if (json.hasOwnProperty('error')) {
    message.value = generateErrorAlertText(json)
    return;
  }
  switch (json['stat']) {
    case 'expire' :
      message.value = translatePhrase("Reset request code has expired")
      break;
    case 'bad' :
      message.value = translatePhrase("Reset request code is wrong")
      break;
    case 'good' :
      message.value = translatePhrase("Enter New Password")
      showState.value = SHOW_ENTER_NEW_PASSWORD
      break
  }
}

function verifyPasswordResetCode() {
  message.value = translateEnglishWords("checking") + "...."
  var formData = { 
    e : email.value,
    code : resetCode.value
  }
  //console.log("formdata",formData)
  useGetData("verbal_password_reset_verify_check.php", formData, processVerifyPasswordResetCodeJsonResponse)
}
  
function processPasswordResetJsonResponse(json) {
  console.log(" in callback",json)
  if (json.hasOwnProperty('error')) {
      message.value = generateErrorAlertText(json)
      return;
  }
  switch (json['stat']) {
    case 'g' :
      message.value = translatePhrase("Password Reset. Please sign in with the new password.")
      toolbarElements.value = originalToolbarElements
      toolbarTrigger.value++
      menuTrigger.value++
      showState.value = SHOW_LOGIN
      break
    default :
      message.value = json['stat']
      break;
  }
}

function doReset() {
  if (password.value == "") {
    message.value = translatePhrase("Please enter your password")
    return
  }
  if (password2.value != password.value) {
    message.value = translatePhrase("Passwords don't match")
    return
  }
  message.value = translatePhrase("resetting password") + "...."
  var formData = { 
    e : email.value , 
    v : resetCode.value, 
    p: password.value,
    reset_cookie : (resetCookie.value) ? "y" : "n"
  }
  //console.log("password reset formdata",formData)
  useGetData("verbal_password_reset_with_code.php", formData, processPasswordResetJsonResponse)
}
</script>

<style scoped>

ion-item {
  margin:10px;
}
</style>